import { Location } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ReportApiService } from '../../report-api.service';
import { ReportExportService } from '../../report-export.service';
import {
  ReportContext,
  ReportDefinition,
  ReportColumn,
  StandardReportRow,
} from '../../report.models';
import { toEnglishDigits } from '../../report.utils';
import { buildSearchIndex, DEFAULT_PAGE_SIZE, formatReportMinutes, PAGE_SIZE_OPTIONS, sumReportField } from './standard-table-report.utils';

@Component({
  selector: 'app-standard-table-report',
  templateUrl: './standard-table-report.component.html',
})
export class StandardTableReportComponent implements OnChanges {
  @Input() context!: ReportContext;
  @Input() definition!: ReportDefinition;

  rows: StandardReportRow[] = [];
  filteredRows: StandardReportRow[] = [];
  pagedRows: StandardReportRow[] = [];
  searchText = '';
  currentPage = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  totalPages = 1;
  totalDistance = 0;
  totalFuel = 0;
  loading = false;
  hasError = false;
  totals: Partial<Record<keyof StandardReportRow, number>> = {};
  totalTime = '0:00';
  private requestVersion = 0;

  constructor(
    private readonly api: ReportApiService,
    private readonly exportService: ReportExportService,
    private readonly gurdService: GurdServiceService,
    private readonly notificationService: NotificationService,
    private readonly otherService: OtherService,
    private readonly router: Router,
    private readonly location: Location,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['context'] || changes['definition']) &&
      this.context &&
      this.definition?.endpoint
    ) {
      this.searchText = '';
      this.requestVersion++;
      this.loading = false;
      this.loadReport();
    }
  }

  async loadReport(): Promise<void> {
    if (this.loading) return;
    const requestVersion = ++this.requestVersion;
    this.loading = true;
    this.hasError = false;
    try {
      const result = await this.api.loadStandardReport(
        this.definition,
        this.context,
        this.gurdService.decrypt(localStorage.getItem('ID')!),
        this.gurdService.decrypt(localStorage.getItem('Session')!),
      );
      if (requestVersion !== this.requestVersion) return;
      if (!Array.isArray(result) && (result?.ISSessionExpired || result?.ISessionExpired)) {
        this.notificationService.showError('لطفا وارد سیستم شوید');
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], {
          queryParams: { report: this.definition.loginReport || this.context.id },
        });
        return;
      }
      const rows = Array.isArray(result) ? result : [];
      this.rows = rows.map((row) => ({
        ...row,
        fGasPer100:
          this.context.id === '2'
            ? Math.floor(Number(row.fGasPer100) || 0)
            : row.fGasPer100,
        searchIndex: buildSearchIndex(row),
      }));
      this.totalDistance = this.roundToTwo(
        this.rows.reduce(
          (total, row) => total + (Number(row.fDistance) || 0),
          0,
        ),
      );
      this.totalFuel = this.roundToTwo(
        this.rows.reduce(
          (total, row) => total + (Number(row.fGasPer100) || 0),
          0,
        ),
      );
      this.refreshTable(true);
    } catch {
      if (requestVersion !== this.requestVersion) return;
      this.rows = [];
      this.hasError = true;
      this.refreshTable(true);
      this.notificationService.showError('خطای داخلی رخ داده است !');
    } finally {
      if (requestVersion === this.requestVersion) this.loading = false;
    }
  }

  onSearchChange(): void {
    this.refreshTable(true);
  }

  clearSearch(): void {
    this.searchText = '';
    this.refreshTable(true);
  }

  get hasActiveFilters(): boolean {
    return !!this.searchText.trim();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.refreshTable(true);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.refreshTable();
  }

  exportExcel(): void {
    if (this.definition.columns) {
      this.exportService.exportRows(
        this.definition.fileName,
        ['#', ...(this.context.id === '4' ? ['تاریخ شروع', 'تاریخ پایان'] : []), ...this.definition.columns.map((column) => column.title), ...(this.context.id === '4' ? ['Imei'] : [])],
        this.filteredRows.map((row, index) => [
          index + 1,
          ...(this.context.id === '4' ? [this.context.startDate, this.context.endDate] : []),
          ...this.definition.columns.map((column) => this.cellValue(row, column)),
          ...(this.context.id === '4' ? [row.strUnitID ?? ''] : []),
        ]),
      );
      return;
    }
    this.exportService.exportTable(this.tableId, this.definition.fileName);
  }

  cellValue(row: StandardReportRow, column: ReportColumn): string | number {
    if (column.format === 'plate') return row.strVehicleNo ? this.otherService.returnCorrectPlate(row.strVehicleNo) : '';
    if (column.format === 'number') return Number(row[column.field]) || 0;
    return row[column.field] == null ? '' : String(row[column.field]);
  }

  totalStartIndex(columns: ReportColumn[]): number {
    const index = columns.findIndex((column) => column.total || column.summary);
    return index < 0 ? columns.length : index;
  }

  goBack(): void {
    this.location.back();
  }

  get tableId(): string {
    return `standard-report-${this.context.id}`;
  }
  get printSectionId(): string {
    return `standard-report-print-${this.context.id}`;
  }
  get subtitle(): string {
    return `از تاریخ ${this.context.startDate} الی ${this.context.endDate}`;
  }

  get searchPlaceholder(): string {
    return `جستجو در جدول: ${(this.definition.searchFields ?? []).join('، ')}...`;
  }

  get printedBy(): string { return this.gurdService.decrypt(localStorage.getItem('FullName')!) || ''; }
  get printDate(): string { return this.otherService.getCurrentDate(); }
  private refreshTable(resetPage = false): void {
    if (resetPage) this.currentPage = 1;
    const query = toEnglishDigits(this.searchText).trim().toLowerCase();
    this.filteredRows = query
      ? this.rows.filter((row) => row.searchIndex?.includes(query))
      : this.rows;
    this.totals = {};
    for (const column of this.definition.columns ?? []) {
      if (column.total) this.totals[column.field] = sumReportField(this.filteredRows, column.field);
    }
    this.totalTime = formatReportMinutes(sumReportField(this.filteredRows, 'iTotalTime'));
    this.totalPages = Math.max(
      1,
      Math.ceil(this.filteredRows.length / this.pageSize),
    );
    this.currentPage = Math.min(this.currentPage, this.totalPages);
    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, start + this.pageSize);
  }

  private roundToTwo(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}
